/**
 * Formatting helpers for `document_page_mapping` document types.
 *
 * Converts the snake_case `doc_type` stored on the order (e.g.
 * "lab_reports_cbc_panel") into a human-readable label for the document list.
 * @module documentTypeLabel
 */

/**
 * Medical acronyms that should render fully uppercased rather than title-cased.
 * Keys are lowercase tokens; membership triggers uppercasing.
 */
const ACRONYMS = new Set([
  "cbc",
  "ucbc",
  "cmp",
  "bmp",
  "ekg",
  "ecg",
  "mri",
  "ct",
  "pet",
  "hiv",
  "psa",
  "tsh",
  "hba1c",
  "ldh",
  "inr",
  "bun",
  "wbc",
  "rbc",
  "pdf",
]);

/**
 * Humanizes a single snake_case token, uppercasing known acronyms.
 *
 * @param token - Lowercase word from the doc type
 * @returns The display-cased token
 */
const formatToken = (token: string): string => {
  if (!token) return token;
  if (ACRONYMS.has(token)) return token.toUpperCase();
  return token.charAt(0).toUpperCase() + token.slice(1);
};

/**
 * Converts a snake_case `doc_type` into a human-readable label.
 *
 * @param docType - The raw doc type (e.g. "lab_reports_cbc_panel"), may be empty
 * @returns The formatted label (e.g. "Lab Reports CBC Panel"), or "" when absent
 */
export const formatDocType = (docType?: string): string => {
  if (!docType) return "";
  return docType
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(formatToken)
    .join(" ");
};
